import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowPathIcon,
  DocumentPlusIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";

interface WcOrderRow {
  id: number;
  number?: string;
  status?: string;
  total?: string;
  billing?: { email?: string; first_name?: string; last_name?: string };
}

interface WcLinkRow {
  wc_order_id: number;
  order_number?: string;
  sync_status?: string;
  invoice_number?: string | null;
  order_total?: number;
  customer_email?: string;
}

export const WooCommercePanel: React.FC = () => {
  const [orders, setOrders] = useState<WcOrderRow[]>([]);
  const [links, setLinks] = useState<WcLinkRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await apiService.getWooCommerceOrders();
    setLoading(false);
    if (res.error) {
      setError(res.message || res.error);
      return;
    }
    const d = res.data as { orders?: WcOrderRow[]; links?: WcLinkRow[]; error?: string };
    if (d?.error) setError(d.error);
    setOrders(d?.orders || []);
    setLinks(d?.links || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setMessage(null);
    const res = await apiService.syncWooCommerceOrders(false);
    setSyncing(false);
    if (res.error) setError(res.message || res.error);
    else {
      const d = res.data as { synced?: number };
      setMessage(`Sincronizados ${d?.synced ?? 0} pedido(s)`);
      load();
    }
  };

  const handleInvoice = async (orderId: number) => {
    const res = await apiService.createInvoiceFromWooCommerceOrder(orderId);
    if (res.error) setError(res.message || res.error);
    else {
      const d = res.data as { invoice_number?: string; duplicate?: boolean };
      setMessage(
        d?.duplicate
          ? `Ya facturado: ${d.invoice_number}`
          : `Factura creada: ${d?.invoice_number}`
      );
      load();
    }
  };

  const linkFor = (orderId: number) => links.find((l) => Number(l.wc_order_id) === orderId);

  const displayOrders =
    orders.length > 0
      ? orders
      : links.map((l) => ({
          id: l.wc_order_id,
          number: l.order_number,
          total: String(l.order_total ?? ""),
          status: l.sync_status,
          billing: { email: l.customer_email },
        }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-violet-50 p-2">
            <ShoppingCartIcon className="h-7 w-7 text-violet-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-zinc-900">Tienda online · WooCommerce</h2>
            <p className="text-sm text-zinc-500">Pedidos y facturación desde su tienda</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSync}
          disabled={syncing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-500 disabled:opacity-60"
        >
          <ArrowPathIcon className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
          Sincronizar pedidos
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
      )}
      {message && (
        <p className="text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2">{message}</p>
      )}

      {loading ? (
        <p className="text-zinc-500">Cargando pedidos…</p>
      ) : displayOrders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-200 p-8 text-center text-zinc-500 text-sm">
          No hay pedidos. Conecte la tienda en Ajustes o use el modo demo.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 text-left text-zinc-600">
              <tr>
                <th className="px-4 py-3 font-medium">Pedido</th>
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium">Factura</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {displayOrders.map((o) => {
                const link = linkFor(Number(o.id));
                const hasInvoice = !!link?.invoice_number;
                return (
                  <tr key={o.id} className="hover:bg-zinc-50/80">
                    <td className="px-4 py-3 font-medium">#{o.number || o.id}</td>
                    <td className="px-4 py-3 text-zinc-600">
                      {o.billing?.email ||
                        [(o.billing as { first_name?: string; last_name?: string })?.first_name, (o.billing as { first_name?: string; last_name?: string })?.last_name].filter(Boolean).join(" ") ||
                        "—"}
                    </td>
                    <td className="px-4 py-3">{o.total ? `${o.total} €` : "—"}</td>
                    <td className="px-4 py-3 capitalize">{o.status || link?.sync_status}</td>
                    <td className="px-4 py-3">{link?.invoice_number || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {!hasInvoice && (
                        <button
                          type="button"
                          onClick={() => handleInvoice(Number(o.id))}
                          className="inline-flex items-center gap-1 text-violet-600 font-medium hover:underline"
                        >
                          <DocumentPlusIcon className="h-4 w-4" />
                          Facturar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WooCommercePanel;
